import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';
import { Heart, Sprout, Compass, Wind, Coins, Sparkles, Mic, ArrowRight, Loader2 } from 'lucide-react';
import { FlippableCard, TarotCardFront } from '../components/TarotCard';
import api from '../lib/api';
import Navigation from '../components/Navigation';
import { INTENTIONS, ROTATING_SUBTEXTS } from '../data/tarotDeck';

const ICON_MAP = { Heart, Sprout, Compass, Wind, Coins, Sparkles };

export default function ReadingFlowPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [phase, setPhase] = useState('question'); // question, drawing, reading
  const [intention, setIntention] = useState('');
  const [question, setQuestion] = useState('');
  const [subtext] = useState(() => ROTATING_SUBTEXTS[Math.floor(Math.random() * ROTATING_SUBTEXTS.length)]);
  const [spreadType, setSpreadType] = useState('the_mirror');
  const [cards, setCards] = useState([]);
  const [flippedCards, setFlippedCards] = useState([]);
  const [readingData, setReadingData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [surpriseMsg, setSurpriseMsg] = useState('');
  const [activeCardIdx, setActiveCardIdx] = useState(0);
  const [isListening, setIsListening] = useState(false);
  const readingRef = useRef(null);

  // Check access
  useEffect(() => {
    checkAccess();
  }, []);

  const checkAccess = async () => {
    try {
      const { data } = await api.get('/tarot/check-access');
      if (!data.has_access) navigate('/paywall');
    } catch (e) {
      console.error(e);
    }
  };

  // Voice input
  const startListening = () => {
    if (!('webkitSpeechRecognition' in window) && !('SpeechRecognition' in window)) {
      setError('Voice input not supported in this browser');
      return;
    }
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    const recognition = new SpeechRecognition();
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.lang = 'en-US';
    recognition.onstart = () => setIsListening(true);
    recognition.onresult = (e) => {
      const transcript = e.results[0][0].transcript;
      setQuestion(prev => prev + (prev ? ' ' : '') + transcript);
    };
    recognition.onerror = () => setIsListening(false);
    recognition.onend = () => setIsListening(false);
    recognition.start();
  };

  const handleDrawCards = async () => {
    if (!question.trim() || !intention) return;
    setLoading(true);
    setError('');
    try {
      const { data } = await api.post('/tarot/reading', { question, intention, spread_type: spreadType });
      setCards(data.cards);
      setReadingData(data);
      setFlippedCards([]);
      setPhase('drawing');
      if (data.surprise_message) setSurpriseMsg(data.surprise_message);
    } catch (e) {
      if (e.response?.status === 402) {
        navigate('/paywall');
      } else {
        setError(e.response?.data?.detail || 'Something went wrong. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const flipCard = (idx) => {
    if (flippedCards.includes(idx)) return;
    setFlippedCards(prev => [...prev, idx]);
    if (flippedCards.length + 1 === 5) {
      setTimeout(() => setPhase('reading'), 1500);
    }
  };

  const positions = readingData?.positions || ['Situation', 'Challenge', 'Subconscious', 'Advice', 'Outcome'];

  return (
    <div className="min-h-screen bg-[#070A1A]" data-testid="reading-flow-page">
      <Navigation />
      <div className="px-4 md:px-12 lg:px-24 py-8 pb-24 md:pb-8 max-w-5xl mx-auto">
        <AnimatePresence mode="wait">
          {/* ─── QUESTION PHASE ─── */}
          {phase === 'question' && (
            <motion.div key="question" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="text-center mb-8">
                <h1 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#FFFFF0]" data-testid="reading-greeting">
                  Hello, {user?.username || 'dear one'}. What's weighing on your heart today?
                </h1>
                <p className="text-sm text-[#BDB2C4] mt-3 font-['Playfair_Display'] italic">{subtext}</p>
              </div>

              {/* Healing Intentions */}
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-8" data-testid="intention-tabs">
                {INTENTIONS.map(intent => {
                  const Icon = ICON_MAP[intent.icon] || Sparkles;
                  const isSelected = intention === intent.id;
                  return (
                    <button
                      key={intent.id}
                      onClick={() => setIntention(intent.id)}
                      data-testid={`intention-${intent.id}`}
                      className={`glass-card p-4 text-left transition-all duration-300 ${isSelected ? 'border-[#D4AF37]/50 bg-[#D4AF37]/5 shadow-[0_0_20px_rgba(212,175,55,0.1)]' : 'hover:border-white/20'}`}
                    >
                      <Icon size={20} className={isSelected ? 'text-[#D4AF37]' : 'text-[#BDB2C4]'} strokeWidth={1.5} />
                      <p className={`text-sm mt-2 ${isSelected ? 'text-[#FFFFF0]' : 'text-[#BDB2C4]'}`}>{intent.label}</p>
                      {isSelected && <p className="text-xs text-[#D4AF37]/70 mt-1 font-['Playfair_Display'] italic">{intent.affirmation}</p>}
                    </button>
                  );
                })}
              </div>

              {/* Question Input */}
              <div className="glass-card p-6 mb-6" data-testid="question-input-area">
                <div className="relative">
                  <textarea
                    data-testid="question-textarea"
                    value={question}
                    onChange={e => setQuestion(e.target.value)}
                    placeholder="Type your question, or simply describe what you're feeling right now..."
                    className="w-full h-32 bg-transparent text-[#FFFFF0] placeholder:text-[#BDB2C4]/40 resize-none focus:outline-none text-base leading-relaxed"
                  />
                  <button
                    onClick={startListening}
                    data-testid="voice-input-btn"
                    className={`absolute bottom-2 right-2 p-2 rounded-full transition-all duration-300 ${isListening ? 'bg-[#C87886]/30 text-[#C87886] animate-pulse' : 'bg-white/5 text-[#BDB2C4] hover:text-[#D4AF37]'}`}
                  >
                    <Mic size={18} />
                  </button>
                </div>
                <p className="text-xs text-[#BDB2C4]/40 mt-2 italic">There are no wrong questions here.</p>
              </div>

              {error && <p className="text-sm text-[#C87886] text-center mb-4" data-testid="reading-error">{error}</p>}

              <div className="text-center">
                <button
                  onClick={handleDrawCards}
                  disabled={loading || !question.trim() || !intention}
                  className="btn-primary inline-flex items-center gap-3 px-10 py-4 text-base disabled:opacity-30 disabled:cursor-not-allowed"
                  data-testid="draw-cards-btn"
                >
                  {loading ? (
                    <><Loader2 size={18} className="animate-spin" /> The cards are listening...</>
                  ) : (
                    <><Sparkles size={18} /> Draw My Cards <ArrowRight size={18} /></>
                  )}
                </button>
              </div>
            </motion.div>
          )}

          {/* ─── DRAWING PHASE ─── */}
          {phase === 'drawing' && (
            <motion.div key="drawing" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
              <div className="text-center mb-8">
                <h2 className="font-['Playfair_Display'] text-2xl text-[#FFFFF0]">
                  Breathe in. Let the cards feel your energy.
                </h2>
                <p className="text-sm text-[#BDB2C4] mt-2">When you're ready... tap each card to reveal.</p>
                <p className="text-xs text-[#D4AF37] mt-1">{flippedCards.length} of 5 revealed</p>
              </div>

              <div className="flex flex-wrap justify-center gap-4 md:gap-6" data-testid="card-draw-area">
                {cards.map((card, i) => (
                  <div key={i} className="flex flex-col items-center gap-2">
                    <p className="text-xs text-[#D4AF37] tracking-widest uppercase">{positions[i]}</p>
                    <FlippableCard
                      card={card}
                      position={positions[i]}
                      index={i}
                      isFlipped={flippedCards.includes(i)}
                      onFlip={() => flipCard(i)}
                    />
                  </div>
                ))}
              </div>

              {flippedCards.length === 5 && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center mt-8">
                  <p className="text-sm text-[#BDB2C4]">All cards revealed. Preparing your reading...</p>
                </motion.div>
              )}
            </motion.div>
          )}

          {/* ─── READING PHASE ─── */}
          {phase === 'reading' && readingData && (
            <motion.div key="reading" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} ref={readingRef}>
              <div className="text-center mb-8">
                <h2 className="font-['Playfair_Display'] text-2xl md:text-3xl text-[#FFFFF0]">Your Reading</h2>
                <p className="text-sm text-[#D4AF37] mt-1">{readingData.spread_name}</p>
              </div>

              {/* Card tabs */}
              <div className="flex justify-center gap-2 mb-8 flex-wrap" data-testid="reading-card-tabs">
                {cards.map((card, i) => (
                  <button
                    key={i}
                    onClick={() => setActiveCardIdx(i)}
                    data-testid={`reading-tab-${i}`}
                    className={`px-4 py-2 rounded-full text-xs tracking-wider transition-all duration-300 ${activeCardIdx === i ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/40' : 'bg-white/5 text-[#BDB2C4] border border-white/10 hover:border-white/20'}`}
                  >
                    {positions[i]}
                  </button>
                ))}
                <button
                  onClick={() => setActiveCardIdx(5)}
                  data-testid="reading-tab-unified"
                  className={`px-4 py-2 rounded-full text-xs tracking-wider transition-all duration-300 ${activeCardIdx === 5 ? 'bg-[#D4AF37]/20 text-[#D4AF37] border border-[#D4AF37]/40' : 'bg-white/5 text-[#BDB2C4] border border-white/10 hover:border-white/20'}`}
                >
                  Full Story
                </button>
              </div>

              {/* Card interpretation */}
              {activeCardIdx < 5 && (
                <motion.div key={activeCardIdx} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="glass-card p-6 md:p-8 mb-6" data-testid="card-interpretation">
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-shrink-0 flex justify-center">
                      <TarotCardFront card={cards[activeCardIdx]} position={positions[activeCardIdx]} index={activeCardIdx} size="small" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-['Playfair_Display'] text-xl text-[#FFFFF0] mb-1">
                        {cards[activeCardIdx]?.name}
                        {cards[activeCardIdx]?.is_reversed && <span className="text-sm text-[#C87886] ml-2 italic">Reversed</span>}
                      </h3>
                      <p className="text-xs text-[#D4AF37] uppercase tracking-widest mb-4">{positions[activeCardIdx]}</p>
                      <p className="text-sm text-[#BDB2C4] leading-relaxed">
                        {readingData.reading?.card_interpretations?.[activeCardIdx]?.interpretation}
                      </p>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Unified Narrative */}
              {activeCardIdx === 5 && (
                <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} className="space-y-6" data-testid="unified-narrative">
                  <div className="glass-card p-6 md:p-8">
                    <h3 className="font-['Playfair_Display'] text-xl text-[#FFFFF0] mb-4">The Full Story</h3>
                    <p className="text-sm text-[#BDB2C4] leading-relaxed">{readingData.reading?.unified_narrative}</p>
                  </div>
                  <div className="glass-card p-6 md:p-8 border-[#D4AF37]/20" style={{ background: 'rgba(212,175,55,0.03)' }}>
                    <p className="text-xs text-[#D4AF37] tracking-widest uppercase mb-3">Your Affirmation</p>
                    <p className="text-base text-[#FFFFF0] font-['Playfair_Display'] italic">{readingData.reading?.affirmation}</p>
                  </div>
                  {/* Reflection Box */}
                  <div className="glass-card p-6 md:p-8 border-[#C87886]/20" style={{ background: 'rgba(200,120,134,0.03)' }} data-testid="reflection-box">
                    <p className="text-xs text-[#C87886] tracking-widest uppercase mb-3">Your Reflection</p>
                    <p className="text-sm text-[#BDB2C4] leading-relaxed italic">{readingData.reading?.reflection_exercise}</p>
                    <p className="text-sm text-[#FFFFF0] mt-4 font-['Playfair_Display']">Tonight, ask yourself: {readingData.reading?.journal_prompt}</p>
                  </div>
                </motion.div>
              )}

              {/* Surprise message */}
              {surpriseMsg && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1 }} className="glass-card p-4 mt-6 border-[#D4AF37]/30 text-center" data-testid="surprise-message">
                  <p className="text-sm text-[#D4AF37] font-['Playfair_Display'] italic">{surpriseMsg}</p>
                </motion.div>
              )}

              {/* Actions */}
              <div className="flex justify-center gap-4 mt-8">
                <button onClick={() => { setPhase('question'); setQuestion(''); setIntention(''); setCards([]); setReadingData(null); setFlippedCards([]); setActiveCardIdx(0); }} className="btn-primary" data-testid="new-reading-btn">
                  New Reading
                </button>
                <button onClick={() => navigate('/journal')} className="text-sm text-[#BDB2C4] hover:text-[#D4AF37] transition-colors underline underline-offset-4" data-testid="view-journal-btn">
                  View in Journal
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
